<template>
  <div class="fu-drtab" data-test="fu-drawer-activity">
    <template v-if="reminders">
      <div class="fu-drtab__title">Auto recall reminders<span>{{ reminders.count }}</span></div>
      <p v-if="!reminders.rows || !reminders.rows.length" class="fu-drtab__soft" data-test="fu-drawer-reminders-none">
        <fu-icon name="bell" :size="14" />No auto recall reminders have been sent yet.
      </p>
      <div v-for="r in reminderRows" :key="r.key" class="fu-drtab__item" data-test="fu-drawer-reminder-item">
        <span class="fu-drtab__avatar fu-drtab__avatar--auto"><fu-icon name="bell" :size="14" /></span>
        <div class="fu-drtab__main">
          <div class="fu-drtab__head">
            <span class="fu-drtab__item-title">{{ r.title }}</span>
            <span class="fu-drtab__badge">Auto</span>
            <span v-if="r.attempt" class="fu-drtab__light">Attempt {{ r.attempt }}</span>
            <span class="fu-drtab__when">{{ r.when }}</span>
          </div>
          <div class="fu-drtab__meta">Sent automatically</div>
        </div>
      </div>
      <p v-if="nextReminder" class="fu-drtab__next" data-test="fu-drawer-reminder-next">Next scheduled reminder: <strong>{{ nextReminder }}</strong></p>
      <div class="fu-drtab__title">Follow-up attempts<span>{{ attemptRows.length }}</span></div>
    </template>

    <p v-if="!attemptRows.length" class="fu-drtab__empty" data-test="fu-drawer-activity-empty">No follow-ups yet — this will be the first.</p>

    <div v-for="a in attemptRows" :key="a.key" class="fu-drtab__item" data-test="fu-drawer-activity-item">
      <span class="fu-drtab__avatar"><fu-icon :name="a.icon" :size="14" /></span>
      <div class="fu-drtab__main">
        <div class="fu-drtab__head">
          <span class="fu-drtab__item-title">{{ a.title }}</span>
          <span class="fu-drtab__when">{{ a.when }}</span>
        </div>
        <div v-if="a.by" class="fu-drtab__meta">{{ a.by }}</div>

        <div v-if="editingId !== a.id && a.note" class="fu-drtab__note">
          <span class="fu-drtab__note-text">{{ a.note }}</span>
          <button
            v-if="ctx.canEditNotes"
            type="button"
            class="fu-drtab__edit"
            :aria-label="`Edit the note of ${a.title}`"
            data-test="fu-drawer-edit-note"
            @click="startEdit(a)"
          ><fu-icon name="edit" :size="13" /></button>
        </div>
        <button
          v-else-if="editingId !== a.id && ctx.canEditNotes"
          type="button"
          class="fu-drtab__add-note"
          data-test="fu-drawer-edit-note"
          @click="startEdit(a)"
        >Add a note</button>

        <div v-if="editingId === a.id" class="fu-drtab__editor">
          <label class="fu-drtab__sr" :for="`${uid}-edit`">Note</label>
          <textarea :id="`${uid}-edit`" v-model="draft" rows="3" class="fu-drtab__textarea" data-test="fu-drawer-edit-text"></textarea>
          <div class="fu-drtab__editor-actions">
            <button
              type="button"
              class="fu-drtab__save"
              :disabled="!changed || ctx.savingNoteId === a.id"
              data-test="fu-drawer-edit-save"
              @click="save(a)"
            >Save note</button>
            <button type="button" class="fu-drtab__cancel" data-test="fu-drawer-edit-cancel" @click="editingId = null">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * DrawerActivityTab — "Activity": the follow-up attempts (today's "Follow-up Activity" panel, newest
 * first) with their notes, editable in place when `ctx.canEditNotes` (today's $can('edit',
 * 'followupNote')); and on recall, when the office pays for reminders, today's "Auto Recall Reminder
 * Activity" (labels, attempt badge, sent date, "Next scheduled reminder"). Save is enabled only once
 * the text changed (drawerPayload.editNoteChanged). The container sends tasks/editFollowupNote with
 * drawerPayload.buildEditNotePayload.
 *
 *   tab  the activity tab model (sections 'attempts' and, on recall, 'reminders')
 *   ctx  see DrawerTabs
 * Events: edit-note({ activity, oldNote, newNote }) — `activity` is the row as loaded (not mutated).
 */
import FuIcon from '../FuIcon.vue';
import { editNoteChanged } from '@/utils/followups/drawer/drawerPayload.js';

// Today's getRecallActivityLabel (CreateFollowup.vue:2370).
const REMINDER_LABEL = { TEXT: 'Recall Text Reminder', EMAIL: 'Recall Email Reminder' };
const MEDIUM = { phone: ['phone', 'Call'], sms: ['text', 'Text'], email: ['mail', 'Email'] };

let seq = 0;

export default {
  name: 'DrawerActivityTab',
  components: { FuIcon },
  props: {
    tab: { type: Object, required: true },
    ctx: { type: Object, required: true },
  },
  data() {
    seq += 1;
    return { uid: `fu-dract-${seq}`, editingId: null, draft: '', original: '' };
  },
  computed: {
    sections() {
      return (this.tab.sections || []).reduce((o, s) => { o[s.key] = s; return o; }, {});
    },
    reminders() {
      const r = this.sections.reminders;
      return r && r.shown ? r : null;
    },
    reminderRows() {
      const rows = (this.reminders && this.reminders.rows) || [];
      return rows.map((r, i) => {
        // today's getRecallSentDate
        let sent = r.ReminderSentDate;
        if (r.ReminderType === 'TEXT' && r.SmsSentOn) sent = r.SmsSentOn;
        if (r.ReminderType === 'EMAIL' && r.EmailSentOn) sent = r.EmailSentOn;
        return {
          key: r.RecallReminderID !== undefined ? r.RecallReminderID : i,
          title: REMINDER_LABEL[r.ReminderType] || 'Recall Reminder',
          attempt: r.AttemptsSoFar || null,
          when: sent ? `Sent ${this.ctx.formatDate(sent)}` : '',
        };
      });
    },
    nextReminder() {
      const rows = (this.reminders && this.reminders.rows) || [];
      return rows[0] && rows[0].NextReminderDue ? this.ctx.formatDate(rows[0].NextReminderDue) : '';
    },
    attemptRows() {
      const s = this.sections.attempts;
      const rows = (s && s.rows) || [];
      return rows
        .map((a, i) => ({ a, i }))
        .sort((x, y) => String(y.a.CreatedAt || '').localeCompare(String(x.a.CreatedAt || '')) || y.i - x.i)
        .map(({ a, i }) => {
          const m = MEDIUM[String(a.FollowupMedium || '').toLowerCase()] || ['phone', 'Follow-up'];
          return {
            key: a.TaskID !== undefined ? a.TaskID : `i${i}`,
            id: a.TaskID !== undefined ? a.TaskID : `i${i}`,
            row: a,
            icon: m[0],
            title: a.Attempts ? `${m[1]} · attempt ${a.Attempts}` : m[1],
            when: a.CreatedAt ? this.ctx.formatDate(a.CreatedAt) : '',
            by: a.CreatedByEmpID !== undefined && a.CreatedByEmpID !== null ? this.ctx.employeeName(a.CreatedByEmpID) : '',
            note: a.FollowupNote || '',
          };
        });
    },
    changed() {
      return editNoteChanged({ FollowupNote: this.draft }, this.original);
    },
  },
  methods: {
    startEdit(a) {
      this.editingId = a.id;
      this.original = a.row.FollowupNote;
      this.draft = a.row.FollowupNote || '';
    },
    save(a) {
      if (!this.changed) return;
      this.$emit('edit-note', { activity: a.row, oldNote: this.original, newNote: this.draft });
      this.editingId = null;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './drawer';

.fu-drtab__title {
  @include fu-dr-section-title;
  span { color: $fu-text-subtle; }
}

.fu-drtab__empty { @include fu-dr-empty; }

.fu-drtab__soft {
  @include fu-dr-soft-note;
  align-items: center;
  margin: 10px 0 0;
  .fu-icon { flex: none; color: $fu-text-subtle; }
}

.fu-drtab__item { @include fu-dr-item; }
.fu-drtab__main { flex: 1; min-width: 0; }

.fu-drtab__avatar {
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $fu-brand-50;
  color: $fu-brand-700;
}

.fu-drtab__avatar--auto { background: $fu-neutral-100; color: $fu-text-muted; }

.fu-drtab__head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.fu-drtab__item-title { @include fu-dr-item-title; }

.fu-drtab__badge {
  @include fu-pill($fu-neutral-100, $fu-neutral-600);
  font-size: 10.5px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.fu-drtab__light { font-size: 12.5px; color: $fu-text-subtle; }

.fu-drtab__when {
  margin-left: auto;
  font-size: 12px;
  color: $fu-text-subtle;
  white-space: nowrap;
}

.fu-drtab__meta { @include fu-dr-item-meta; }

.fu-drtab__note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 8px;
  padding: 9px 11px;
  border-radius: 9px;
  background: $fu-neutral-50;
  font-size: 13px;
  line-height: 1.45;
  color: $fu-neutral-700;
}

.fu-drtab__note-text { flex: 1; min-width: 0; white-space: pre-line; overflow-wrap: anywhere; }

.fu-drtab__edit { @include fu-icon-btn(24px); }

.fu-drtab__add-note {
  @include fu-link-btn;
  margin-top: 6px;
  font-size: 12.5px;
}

.fu-drtab__editor { margin-top: 8px; }
.fu-drtab__sr { @include fu-visually-hidden; }
.fu-drtab__textarea { @include fu-dr-textarea; }
.fu-dr--phone .fu-drtab__textarea { @include fu-dr-phone-input; }

.fu-drtab__editor-actions { display: flex; gap: 8px; margin-top: 8px; }
.fu-drtab__save { @include fu-btn(primary, sm); }
.fu-drtab__cancel { @include fu-btn(ghost, sm); }

.fu-drtab__next {
  margin: 10px 0 0;
  font-size: 12.5px;
  color: $fu-text-muted;
  strong { color: $fu-text; }
}
</style>
